import React, { useEffect } from "react";
import { Card, Badge, Grid } from "antd";
import { useTranslation } from "react-i18next";
import { useAntdTheme } from "../../shared/hooks";
import { usePendingOrderFilters } from "./hooks/usePendingOrderFilters";
import { usePendingTabState } from "./hooks/usePendingTabState";
import { useCampaignOrderGroups } from "./hooks/useCampaignOrderGroups";
import PendingOrderCol from "./PendingOrderCol";
import DoneOrderCol from "./DoneOrderCol";
import OrderDetails from "./OrderDetails";

interface PendingOrderTabsProps {
  pendingOrders: any[];
  doneOrders: any[];
  restaurantId: string;
  renderOrder: (order: any) => React.ReactNode;
  renderOrderTitle: (order: any) => React.ReactNode;
}

export const PendingOrderTabs: React.FC<PendingOrderTabsProps> = ({
  pendingOrders,
  doneOrders,
  restaurantId,
  renderOrder,
  renderOrderTitle,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const screens = Grid.useBreakpoint();

  // Campaign orders get their own pending/done tabs instead of mixing into the
  // time-based tabs, where they would otherwise crowd the "> 24 hr" tab.
  const { regularPending, regularDone, campaigns } = useCampaignOrderGroups(
    restaurantId,
    pendingOrders,
    doneOrders
  );

  const {
    immediate: immediatePendingOrders,
    onDeck: onDeckPendingOrders,
    today: todayPendingOrders,
    future: futurePendingOrders,
    onDeckIntervalMinutes,
    todayIntervalHours,
  } = usePendingOrderFilters(regularPending);

  const { activeTabKey, onActiveTabChange } = usePendingTabState();

  const createTabLabel = (text: string, count: number, badgeColor: string) => (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: token.marginSM,
        fontWeight: 600,
      }}
    >
      <span>{text}</span>
      {count > 0 && <Badge count={count} style={{ backgroundColor: badgeColor }} />}
    </span>
  );

  const tabList = [
    {
      key: "pending-immediate",
      label: createTabLabel(t("immediate"), immediatePendingOrders.length, token.colorError),
    },
    {
      key: "pending-on-deck",
      label: createTabLabel(
        t("within_minutes", { minutes: onDeckIntervalMinutes }),
        onDeckPendingOrders.length,
        token.colorWarning
      ),
    },
    {
      key: "pending-today",
      label: createTabLabel(
        t("more_than_minutes", { minutes: onDeckIntervalMinutes }),
        todayPendingOrders.length,
        token.colorInfo
      ),
    },
    {
      key: "done",
      label: createTabLabel(t("done"), regularDone.length, token.colorSuccess),
    },
    {
      key: "pending-future",
      label: createTabLabel(
        t("more_than_hours", { hours: todayIntervalHours }),
        futurePendingOrders.length,
        token.colorTextDisabled
      ),
    },
    ...campaigns.flatMap((campaign) => {
      const name = campaign.name ?? t("campaign");
      return [
        {
          key: `campaign-pending:${campaign.id}`,
          label: createTabLabel(
            t("campaign_pending", { name }),
            campaign.pending.length,
            token.colorTextDisabled
          ),
        },
        {
          key: `campaign-done:${campaign.id}`,
          label: createTabLabel(
            t("campaign_done", { name }),
            campaign.done.length,
            token.colorSuccess
          ),
        },
      ];
    }),
  ];

  // A campaign's tabs disappear once it has no pending or done orders left, so
  // fall back to the default tab rather than showing an unselected tab bar.
  const activeTabExists = tabList.some((tab) => tab.key === activeTabKey);
  useEffect(() => {
    if (!activeTabExists) {
      onActiveTabChange("pending-immediate");
    }
  }, [activeTabExists, onActiveTabChange]);

  const getCampaign = (tabKey: string) => {
    const campaignId = tabKey.slice(tabKey.indexOf(":") + 1);
    return campaigns.find((c) => c.id === campaignId);
  };

  const getTabSize = (): "large" | "middle" | "small" => {
    if (screens.xl || screens.lg) return "large";
    if (screens.md) return "middle";
    return "small";
  };

  const getTabContent = () => {
    switch (activeTabKey) {
      case "pending-immediate":
        return (
          <PendingOrderCol
            orders={immediatePendingOrders}
            renderOrder={renderOrder}
            renderOrderTitle={renderOrderTitle}
            renderOrderDetails={(order) => (
              <OrderDetails order={order} pending restaurantId={restaurantId} />
            )}
            restaurantId={restaurantId}
            elemsMaxHeight="calc(100vh - 200px)"
            playSound={true}
          />
        );
      case "pending-on-deck":
        return (
          <PendingOrderCol
            orders={onDeckPendingOrders}
            renderOrder={renderOrder}
            renderOrderTitle={renderOrderTitle}
            renderOrderDetails={(order) => (
              <OrderDetails order={order} pending restaurantId={restaurantId} />
            )}
            restaurantId={restaurantId}
            elemsMaxHeight="calc(100vh - 200px)"
          />
        );
      case "pending-today":
        return (
          <PendingOrderCol
            orders={todayPendingOrders}
            renderOrder={renderOrder}
            renderOrderTitle={renderOrderTitle}
            renderOrderDetails={(order) => (
              <OrderDetails order={order} pending restaurantId={restaurantId} />
            )}
            restaurantId={restaurantId}
            elemsMaxHeight="calc(100vh - 200px)"
          />
        );
      case "done":
        return (
          <DoneOrderCol
            orders={regularDone}
            renderOrder={renderOrder}
            renderOrderTitle={renderOrderTitle}
            renderOrderDetails={(order) => (
              <OrderDetails order={order} restaurantId={restaurantId} />
            )}
            elemsMaxHeight="calc(100vh - 200px)"
          />
        );
      case "pending-future":
        return (
          <PendingOrderCol
            orders={futurePendingOrders}
            renderOrder={renderOrder}
            renderOrderTitle={renderOrderTitle}
            renderOrderDetails={(order) => (
              <OrderDetails order={order} pending restaurantId={restaurantId} />
            )}
            restaurantId={restaurantId}
            elemsMaxHeight="calc(100vh - 200px)"
          />
        );
      default:
        if (activeTabKey.startsWith("campaign-pending:")) {
          return (
            <PendingOrderCol
              orders={getCampaign(activeTabKey)?.pending ?? []}
              renderOrder={renderOrder}
              renderOrderTitle={renderOrderTitle}
              renderOrderDetails={(order) => (
                <OrderDetails order={order} pending restaurantId={restaurantId} />
              )}
              restaurantId={restaurantId}
              elemsMaxHeight="calc(100vh - 200px)"
            />
          );
        }
        if (activeTabKey.startsWith("campaign-done:")) {
          return (
            <DoneOrderCol
              orders={getCampaign(activeTabKey)?.done ?? []}
              renderOrder={renderOrder}
              renderOrderTitle={renderOrderTitle}
              renderOrderDetails={(order) => (
                <OrderDetails order={order} restaurantId={restaurantId} />
              )}
              elemsMaxHeight="calc(100vh - 200px)"
            />
          );
        }
        return null;
    }
  };

  const getTabBackground = () => {
    switch (activeTabKey) {
      case "pending-immediate":
        return token.colorBgContainer;
      case "pending-on-deck":
      case "pending-today":
      case "pending-future":
        return token.colorError;
      case "done":
        return token.colorSuccessBg;
      default:
        if (activeTabKey.startsWith("campaign-pending:")) {
          return token.colorError;
        }
        if (activeTabKey.startsWith("campaign-done:")) {
          return token.colorSuccessBg;
        }
        return token.colorBgContainer;
    }
  };

  return (
    <Card
      className="control-station-tabs"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
      styles={{
        header: {
          padding: `0 ${token.paddingLG}px`,
          margin: 0,
          background: token.colorBgLayout,
        },
        body: {
          padding: token.paddingMD,
          height: "100%",
          background: getTabBackground(),
          flex: 1,
        },
      }}
      tabList={tabList}
      activeTabKey={activeTabKey}
      onTabChange={onActiveTabChange}
      tabProps={{ size: getTabSize() }}
    >
      {getTabContent()}
    </Card>
  );
};
